import { Controller } from "@hotwired/stimulus"

// Picking photos for a post.
//
// Photos are downscaled in the browser before upload. The server publishes at
// 1400px (ImageProcessingService::MAX_WIDTH), so a 4000px phone original
// spends ~4MB to deliver pixels that get thrown away on arrival — and 45 of
// them is 173MB, over both Cloudflare's 100MB request cap and nginx's.
// Resizing first turns that into roughly 25MB, one upload, with no visible
// difference in the published image.
//
// 2560px rather than 1400px: it leaves headroom if MAX_WIDTH is ever raised,
// and it lets vips do the final downscale with proper Lanczos resampling
// rather than the browser's canvas.
const MAX_EDGE = 2560
const QUALITY = 0.92
// Enough to keep the CPU busy without holding many full bitmaps at once.
const CONCURRENCY = 3

export default class extends Controller {
  static targets = ["input", "preview", "count", "submit", "dropzone"]

  connect() {
    // A DataTransfer is the only way to write back to input.files, which is
    // what lets drops and repeated picks accumulate instead of replacing.
    this.files = new DataTransfer()
    this.urls = []
    this.working = 0
    this.render()
  }

  disconnect() {
    this.releaseUrls()
  }

  // The whole dashed box is the click target, so there's no dead space
  // inside it that silently does nothing.
  browse(event) {
    if (event.target.closest("button, a, input")) return
    this.inputTarget.click()
  }

  selected() {
    this.add(this.inputTarget.files)
  }

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget?.classList.add("border-indigo-500", "bg-indigo-50")
  }

  dragleave(event) {
    event.preventDefault()
    this.dropzoneTarget?.classList.remove("border-indigo-500", "bg-indigo-50")
  }

  drop(event) {
    event.preventDefault()
    this.dropzoneTarget?.classList.remove("border-indigo-500", "bg-indigo-50")
    this.add(event.dataTransfer.files)
  }

  remove(event) {
    event.preventDefault()
    this.files.items.remove(Number(event.params.index))
    this.inputTarget.files = this.files.files
    this.render()
  }

  async add(list) {
    const existing = new Set(
      Array.from(this.files.files).map((f) => this.key(f))
    )
    const incoming = Array.from(list).filter((file) => file.type.startsWith("image/"))

    this.working += incoming.length
    this.render()

    // A small worker pool: 45 photos resized all at once would hold 45
    // decoded bitmaps in memory, which is the problem we're solving.
    const queue = incoming.slice()
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (queue.length) {
        const file = queue.shift()
        const prepared = await this.downscale(file)
        this.working--
        if (!existing.has(this.key(prepared))) {
          existing.add(this.key(prepared))
          this.files.items.add(prepared)
          this.inputTarget.files = this.files.files
        }
        this.render()
      }
    })
    await Promise.all(workers)

    this.working = 0
    this.render()
  }

  // Returns a smaller File, or the original when it's already small enough or
  // anything goes wrong — an upload that's merely large beats no upload.
  async downscale(file) {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" })
      const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
      if (scale === 1) {
        bitmap.close()
        return file
      }

      const width = Math.round(bitmap.width * scale)
      const height = Math.round(bitmap.height * scale)
      const canvas = document.createElement("canvas")
      canvas.width = width
      canvas.height = height
      const context = canvas.getContext("2d")
      context.imageSmoothingEnabled = true
      context.imageSmoothingQuality = "high"
      context.drawImage(bitmap, 0, 0, width, height)
      bitmap.close()

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/webp", QUALITY)
      )
      // Safari used to hand back null for webp; fall back rather than lose the photo.
      if (!blob) return file
      // Only worth it if it actually saved something.
      if (blob.size >= file.size) return file

      const name = file.name.replace(/\.[^.]+$/, "") + ".webp"
      return new File([blob], name, { type: "image/webp", lastModified: file.lastModified })
    } catch (error) {
      console.warn("Could not downscale", file.name, error)
      return file
    }
  }

  key(file) {
    return `${file.name}:${file.size}`
  }

  render() {
    const files = Array.from(this.files.files)

    this.releaseUrls()
    this.previewTarget.innerHTML = ""
    for (const [index, file] of files.entries()) {
      const url = URL.createObjectURL(file)
      this.urls.push(url)

      const wrapper = document.createElement("div")
      wrapper.className = "relative group"
      wrapper.innerHTML = `
        <img src="${url}" class="w-full h-20 object-cover rounded" loading="lazy">
        <button type="button"
                data-action="image-upload#remove"
                data-image-upload-index-param="${index}"
                class="absolute top-0 right-0 hidden group-hover:block bg-black/60 text-white text-xs rounded-bl px-1.5 py-0.5"
                title="Remove">&times;</button>`
      this.previewTarget.appendChild(wrapper)
    }

    if (this.hasCountTarget) {
      const bytes = files.reduce((sum, f) => sum + f.size, 0)
      if (this.working > 0) {
        this.countTarget.textContent = `Preparing photos… ${files.length} of ${files.length + this.working} ready`
      } else if (files.length) {
        this.countTarget.textContent =
          `${files.length} photo${files.length === 1 ? "" : "s"} selected · ${this.megabytes(bytes)} to upload`
      } else {
        this.countTarget.textContent = ""
      }
    }

    // Without this the form happily posts an empty multipart body, which the
    // server can only answer with "No images were received."
    if (this.hasSubmitTarget) {
      const busy = this.working > 0
      const empty = files.length === 0
      this.submitTarget.disabled = busy || empty
      this.submitTarget.classList.toggle("opacity-50", busy || empty)
      this.submitTarget.classList.toggle("cursor-not-allowed", busy || empty)
      this.submitTarget.value = busy
        ? "Preparing photos…"
        : files.length
          ? `Upload ${files.length} image${files.length === 1 ? "" : "s"}`
          : "Upload Images"
    }
  }

  releaseUrls() {
    this.urls.forEach((url) => URL.revokeObjectURL(url))
    this.urls = []
  }

  megabytes(bytes) {
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  }
}
